import React from "react";
import { Lock, Gift, X } from "lucide-react";
import { resolveImageUrl } from "@/lib/images";

export const GOLD = { hi: "#F8E4B6", mid: "#E2B872", deep: "#A37A38" };

const NUMERAL_FONT = "'Cormorant Garamond', Georgia, serif";
const KICKER_FONT = "'Cinzel', Georgia, serif";

const FRONT = {
  available: {
    background: "linear-gradient(145deg, rgba(226,184,114,0.18) 0%, rgba(163,122,56,0.08) 50%, rgba(0,0,0,0.45) 100%)",
    borderColor: "rgba(226,184,114,0.55)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -2px 4px rgba(0,0,0,0.4), 0 6px 18px -8px rgba(0,0,0,0.6)",
  },
  locked: {
    background: "linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.38) 100%)",
    borderColor: "rgba(255,255,255,0.08)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), inset 0 -2px 4px rgba(0,0,0,0.35)",
  },
  missed: {
    background: "linear-gradient(145deg, rgba(255,255,255,0.015) 0%, rgba(0,0,0,0.5) 100%)",
    borderColor: "rgba(255,255,255,0.05)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
  },
  disabled: {
    background: "linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0.38) 100%)",
    borderColor: "rgba(255,255,255,0.08)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10)",
  },
};
FRONT.opened = FRONT.disabled;

const BACK_WIN = {
  background: "linear-gradient(145deg, rgba(245,208,138,0.26) 0%, rgba(184,134,45,0.16) 50%, rgba(26,18,9,0.88) 100%)",
  borderColor: "rgba(226,184,114,0.65)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.3), 0 0 22px -4px rgba(226,184,114,0.45)",
};
const BACK_LOSE = {
  background: "linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(15,20,32,0.82) 100%)",
  borderColor: "rgba(255,255,255,0.10)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
};

// One lacquered door of the coffret. Pure presentation: state + callbacks come from AdventCalendar.
export default function AdventDoor({
  day,
  state,
  opened,
  isOpening,
  reveal,
  isToday,
  hero = false,
  heroClass = "",
  index,
  LoseIcon,
  onOpen,
  image = null,
  t,
}) {
  const isOpen = state === "opened" || isOpening;
  const available = state === "available";
  const front = FRONT[state] || FRONT.disabled;
  const numeralColor = available ? GOLD.hi : state === "missed" ? "rgba(255,255,255,0.22)" : "rgba(255,255,255,0.38)";

  return (
    <button
      type="button"
      disabled={!available || isOpening}
      onClick={() => onOpen(day)}
      className={`advent-door advent-enter relative select-none ${hero ? heroClass : "aspect-square"} ${
        isOpen ? "is-open" : ""
      } ${available ? "cursor-pointer" : "cursor-default"}`}
      style={{ animationDelay: `${index * 22}ms` }}
      data-testid={`advent-door-${day}`}
      data-state={state}
      aria-label={`${t("play.advent_day", "Jour")} ${day}`}
    >
      <div className="advent-door-inner relative h-full w-full">
        {/* Front — closed lacquered door */}
        <div
          className={`advent-face advent-front absolute inset-0 flex flex-col items-center justify-center rounded-[14px] sm:rounded-[18px] border ${
            available ? "advent-available" : ""
          }`}
          style={front}
        >
          {available && isToday && (
            <span
              className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full"
              style={{ background: GOLD.hi, boxShadow: `0 0 8px 2px rgba(226,184,114,0.6)` }}
              aria-hidden="true"
              data-testid={`advent-door-${day}-today`}
            />
          )}
          {hero && (
            <span
              className="mb-0.5 text-[8px] sm:text-[10px] font-bold uppercase tracking-[0.3em]"
              style={{ fontFamily: KICKER_FONT, color: available ? GOLD.mid : "rgba(255,255,255,0.35)" }}
            >
              {t("play.advent_hero_kicker", "Le grand jour")}
            </span>
          )}
          <span
            className={`leading-none font-semibold ${hero ? "text-3xl sm:text-5xl" : "text-xl sm:text-2xl"} ${
              state === "missed" ? "line-through" : ""
            }`}
            style={{
              fontFamily: NUMERAL_FONT,
              fontFeatureSettings: "'lnum' 1, 'tnum' 1",
              color: numeralColor,
              textShadow: available ? "0 1px 4px rgba(226,184,114,0.45)" : "none",
            }}
          >
            {day}
          </span>
          {state === "locked" && <Lock className="mt-1 h-3 w-3 sm:h-3.5 sm:w-3.5 stroke-[1.5] text-white/25" />}
          {state === "missed" && <X className="mt-0.5 h-3 w-3 stroke-[1.5] text-white/20" />}
        </div>

        {/* Back — revealed content (optional door illustration underneath) */}
        <div
          className="advent-face back absolute inset-0 flex flex-col items-center justify-center overflow-hidden rounded-[14px] sm:rounded-[18px] border p-1 text-center"
          style={opened?.is_win ? BACK_WIN : BACK_LOSE}
        >
          {image && (
            <>
              <img
                src={resolveImageUrl(image)}
                alt=""
                className={`absolute inset-0 h-full w-full object-cover ${opened?.is_win ? "" : "opacity-60 saturate-[.7]"}`}
                draggable={false}
                data-testid={`advent-door-${day}-image`}
              />
              <div
                className="absolute inset-0"
                style={{
                  background: opened?.is_win
                    ? "linear-gradient(to top, rgba(26,18,9,0.88) 0%, rgba(26,18,9,0.35) 45%, rgba(0,0,0,0) 75%)"
                    : "linear-gradient(to top, rgba(10,12,20,0.85) 0%, rgba(10,12,20,0.3) 55%, rgba(0,0,0,0) 100%)",
                }}
              />
            </>
          )}
          <div className={`relative flex flex-col items-center ${image ? "mt-auto pb-0.5" : ""}`}>
            {opened?.is_win ? (
              <>
                <Gift
                  className={`${hero ? "h-6 w-6 sm:h-8 sm:w-8" : "h-4 w-4 sm:h-5 sm:w-5"} advent-gift`}
                  style={{ color: GOLD.hi }}
                />
                {reveal && opened?.result_label && (
                  <span
                    className={`mt-1 line-clamp-2 font-semibold uppercase leading-tight tracking-[0.08em] ${
                      hero ? "text-[10px] sm:text-xs" : "text-[7.5px] sm:text-[9px]"
                    }`}
                    style={{ color: GOLD.hi }}
                  >
                    {opened.result_label}
                  </span>
                )}
              </>
            ) : (
              <LoseIcon className={`${hero ? "h-6 w-6" : "h-4 w-4"} stroke-[1.5] text-white/45`} />
            )}
          </div>
        </div>
      </div>
    </button>
  );
}
